import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action } from '@/shared/screen/define';

import { beneficiaryListFilters } from './beneficiary.filters';

// Ações das abas Favorecidos e Meus limites (U2a, U23/U24), acrescentadas ao manifesto do detalhe. Leve:
// o fio entra pela tela.
//
// Ficam fora, de propósito: Adicionar favorecido (o formulário leva documento, conta e chave Pix de
// terceiros, que não passam pelo agente), e Excluir, Cancelar e Detalhes do favorecido (presos a uma
// linha: precisariam de chave de alvo na entrada, que a trava `agent-tool-targets` recusa).

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
const OPEN_TAB_ONLY = 'This action only runs on the bank account detail screen, on the tab that is open there.';
const needsOpenTab = () => {
  throw new Error(OPEN_TAB_ONLY);
};
const none = z.strictObject({});

// ─── Favorecidos ──────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`FavoredHeader.tsx:63-69`): a lista e o saldo da conta (`:28-32`). */
export const beneficiariesRefresh = action({
  id: 'bankAccount.beneficiaries.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo a lista de favorecidos e o saldo da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

export const beneficiariesFilter = filterAction('bankAccount.beneficiaries.filter', beneficiaryListFilters, {
  description: msg`Filtra a lista de favorecidos da conta aberta: fav_searchString (todos os campos), fav_name (nome do favorecido), fav_registrationNumber (CPF/CNPJ, dígitos e letras) e fav_status (Approved, Pending, Rejected, Timeout, Canceled, Error, Processing, Finished). Filtro omitido é limpo.`,
});

export const beneficiariesPage = listPageAction('bankAccount.beneficiaries.page');

/**
 * Prepara o step-up (U24): `stepUp: true`, entrada vazia, e o alvo é a seleção que está na tela. A tela troca
 * o `run` pelo que abre o diálogo de senha e código MFA; nada é aprovado pela ferramenta.
 */
export const preparedAction = (id: string, label: MessageDescriptor, kind: 'write' | 'destructive', description: MessageDescriptor, title: MessageDescriptor) =>
  action({
    id,
    kind,
    label,
    stepUp: true,
    description,
    input: none,
    preview: () => ({ title: i18n._(title), detail: i18n._(msg`Confirme com a sua senha e o código do aplicativo MFA.`), ...(kind === 'destructive' ? { tone: 'destructive' as const } : {}) }),
    run: needsOpenTab,
  });

/** "Aprovar" e "Reprovar" (`FavoredHeader.tsx:42-55`): pedem senha e código MFA (F2a). */
export const beneficiariesApprove = preparedAction(
  'bankAccount.beneficiaries.approve',
  msg`Aprovar`,
  'write',
  msg`Prepara a aprovação dos favorecidos marcados na lista de favorecidos da conta aberta. Só abre o diálogo de senha e código MFA, com a quantidade de favorecidos: esta ferramenta não aprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela, todas aguardando aprovação.`,
  msg`Aprovar favorecido`,
);

export const beneficiariesReject = preparedAction(
  'bankAccount.beneficiaries.reject',
  msg`Reprovar`,
  'destructive',
  msg`Prepara a reprovação dos favorecidos marcados na lista de favorecidos da conta aberta. Só abre o diálogo de senha e código MFA, com a quantidade de favorecidos: esta ferramenta não reprova nada. Não recebe entrada; os alvos são as linhas marcadas na tela, todas aguardando aprovação.`,
  msg`Reprovar favorecido`,
);

// ─── Meus limites ─────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`MyLimitsContainer.tsx:99-106`): só os limites. */
export const limitsRefresh = action({
  id: 'bankAccount.limits.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os limites transacionais da conta aberta. Nada é alterado.`,
  run: () => undefined,
});

/**
 * "Solicitar aumento de limite" (`MyLimitsContainer.tsx:107-116`): pede senha e código MFA. A ferramenta só
 * abre o formulário com os limites atuais; os valores, a declaração e a confirmação são da pessoa.
 */
export const limitsRequest = preparedAction(
  'bankAccount.limits.request',
  msg`Solicitar aumento de limite`,
  'write',
  msg`Abre o formulário de aumento de limite da conta aberta, preenchido com os limites atuais. Quem digita os valores, decide sobre a declaração de faturamento e confirma com a senha e o código MFA é a pessoa: esta ferramenta não envia nada. Não recebe entrada.`,
  msg`Solicitar aumento de limite`,
);

/** As ações novas do detalhe, para o `actions` do manifesto. */
export const BENEFICIARY_LIMIT_ACTIONS = [beneficiariesRefresh, beneficiariesFilter, beneficiariesPage, beneficiariesApprove, beneficiariesReject, limitsRefresh, limitsRequest];
